import { Game } from './game/Game.js';
import { Mech } from './entities/Mech.js';
import { createPart } from './parts/PartRegistry.js';
import { getFusionResult } from './systems/Fusion.js';

/**
 * Entry point for SCRAP.
 *
 * Creates and starts the game instance.
 */
function main(): void {
  const game = new Game();

  // Create player's mech at the center of the world
  const mech = new Mech(game.getInput(), game.getRoom(), game, 0, 0);
  game.addEntity(mech);

  // Set player mech for camera follow
  game.setPlayerMech(mech);

  // Generate floor 1 (this will create room, cover, and enemies)
  game.generateFloor(1);

  // Spawn test pickups around the arena for testing interact system
  game.spawnPickupById(5, 0, 'treads'); // Transport on right
  game.spawnPickupById(-5, 0, 'repeater'); // Weapon on left
  game.spawnPickupById(0, 5, 'standard'); // Core above
  game.spawnPickupById(0, -5, 'tracker'); // Head below

  game.start();

  // Expose game instance and mech to window for debugging
  (window as any).game = game;
  (window as any).mech = mech;

  // Add test utilities for energy/HP system
  (window as any).testEnergy = {
    useEnergy: (amount: number) => {
      const result = mech.useEnergy(amount);
      console.log(`Used ${amount} energy: ${result ? 'SUCCESS' : 'FAILED (not enough energy)'}`);
      console.log(`Current energy: ${mech.getEnergy()}/${mech.getMaxEnergy()}`);
      return result;
    },
    takeDamage: (amount: number) => {
      const alive = mech.takeDamage(amount);
      console.log(`Took ${amount} damage: ${alive ? 'still alive' : 'DEAD'}`);
      console.log(`Current HP: ${mech.getHP()}/${mech.getMaxHP()}`);
      return alive;
    },
    equipCore: (coreId: string) => {
      const core = createPart(coreId);
      const old = mech.equipPart(core);
      console.log(`Equipped ${coreId} core (replaced: ${old ? old.data.name : 'none'})`);
      console.log(`New HP: ${mech.getHP()}/${mech.getMaxHP()}`);
      console.log(`New Energy: ${mech.getEnergy()}/${mech.getMaxEnergy()}`);
    },
    removeCore: () => {
      const old = mech.removePart('core');
      console.log(`Removed core: ${old ? old.data.name : 'none was equipped'}`);
      console.log(`New HP: ${mech.getHP()}/${mech.getMaxHP()}`);
      console.log(`New Energy: ${mech.getEnergy()}/${mech.getMaxEnergy()}`);
    },
  };

  // Add test utilities for transport/dash system
  (window as any).testTransport = {
    equipTransport: (transportId: string) => {
      const transport = createPart(transportId);
      const old = mech.equipPart(transport);
      const data = transport.data as any;
      console.log(`Equipped ${transportId} (replaced: ${old ? old.data.name : 'none'})`);
      console.log(`  Move Speed: ${data.moveSpeed} units/sec`);
      console.log(`  Dash Distance: ${data.dashDistance} units`);
      console.log(`  Dash Cooldown: ${data.dashCooldown} sec`);
      console.log('Use Space or Shift while moving to dash!');
    },
    removeTransport: () => {
      const old = mech.removePart('transport');
      console.log(`Removed transport: ${old ? old.data.name : 'none was equipped'}`);
      console.log('Move speed now: 2 units/sec (head-only hop)');
      console.log('Dash disabled');
    },
    testAll: () => {
      console.log('Testing all transport types...');
      console.log('');
      console.log('=== TREADS ===');
      (window as any).testTransport.equipTransport('treads');
      console.log('');
      setTimeout(() => {
        console.log('=== HOVER ===');
        (window as any).testTransport.equipTransport('hover');
        console.log('');
      }, 1000);
      setTimeout(() => {
        console.log('=== LEGS ===');
        (window as any).testTransport.equipTransport('legs');
        console.log('');
      }, 2000);
    },
  };

  // Add test utilities for head system (vision radius / fog of war)
  (window as any).testHead = {
    equipHead: (headId: string) => {
      const head = createPart(headId);
      const old = mech.equipPart(head);
      const data = head.data as any;
      console.log(`Equipped ${headId} (replaced: ${old ? old.data.name : 'none'})`);
      console.log(`  Vision Radius: ${data.visionRadius} units`);
      console.log(`  Can See HP: ${data.canSeeHP}`);
      console.log(`  Can See Trails: ${data.canSeeTrails}`);
      console.log(`  Can See Drops: ${data.canSeeDrops}`);
      console.log('Notice how the fog of war circle changes size!');
    },
    testAll: () => {
      console.log('Testing all head types...');
      console.log('');
      console.log('=== TRACKER (Smallest vision) ===');
      (window as any).testHead.equipHead('tracker');
      console.log('');
      setTimeout(() => {
        console.log('=== SCANNER (Medium vision, can see HP) ===');
        (window as any).testHead.equipHead('scanner');
        console.log('');
      }, 3000);
      setTimeout(() => {
        console.log('=== SPOTTER (Largest vision) ===');
        (window as any).testHead.equipHead('spotter');
        console.log('');
      }, 6000);
    },
  };

  // Add test utilities for weapon system
  (window as any).testWeapon = {
    equipWeapon: (weaponId: string) => {
      const weapon = createPart(weaponId);
      const old = mech.equipPart(weapon);
      const data = weapon.data as any;
      console.log(`Equipped ${weaponId} (replaced: ${old ? old.data.name : 'none'})`);
      console.log(`  Fire Rate: ${data.fireRate} shots/sec`);
      console.log(`  Damage: ${data.damage} per shot`);
      console.log(`  Projectile Speed: ${data.projectileSpeed} units/sec`);
      console.log(`  Projectiles per Shot: ${data.projectilesPerShot}`);
      console.log(`  Spread: ${data.spread}°`);
      console.log('Hold left mouse button to fire!');
    },
    removeWeapon: () => {
      const old = mech.removePart('weapon');
      console.log(`Removed weapon: ${old ? old.data.name : 'none was equipped'}`);
      console.log('Cannot shoot without a weapon');
    },
    testAll: () => {
      console.log('Testing all weapon types...');
      console.log('');
      console.log('=== REPEATER (Steady automatic fire) ===');
      (window as any).testWeapon.equipWeapon('repeater');
      console.log('');
      setTimeout(() => {
        console.log('=== LANCER (Slow piercing shots) ===');
        (window as any).testWeapon.equipWeapon('lancer');
        console.log('');
      }, 3000);
      setTimeout(() => {
        console.log('=== SPRAYER (Short range spread) ===');
        (window as any).testWeapon.equipWeapon('sprayer');
        console.log('');
      }, 6000);
    },
  };

  // Add test utilities for partial mech states (scrap-4eo)
  (window as any).testPartial = {
    // Break individual parts
    breakWeapon: () => {
      const old = mech.removePart('weapon');
      console.log(`❌ Weapon broken: ${old ? old.data.name : 'none was equipped'}`);
      console.log('→ Cannot shoot without weapon (try clicking)');
    },
    breakTransport: () => {
      const old = mech.removePart('transport');
      console.log(`❌ Transport broken: ${old ? old.data.name : 'none was equipped'}`);
      console.log('→ Movement speed reduced to 2 units/sec (crawl)');
      console.log('→ Dash disabled (try Space/Shift while moving)');
    },
    breakCore: () => {
      const old = mech.removePart('core');
      console.log(`❌ Core broken: ${old ? old.data.name : 'none was equipped'}`);
      console.log('→ Energy regen reduced to 1 unit/sec (very slow)');
      console.log('→ HP reduced (lost core durability)');
      console.log(`Current HP: ${mech.getHP()}/${mech.getMaxHP()}`);
    },

    // Get current state
    getState: () => {
      const state = {
        hasWeapon: mech.hasPartInSlot('weapon'),
        hasTransport: mech.hasPartInSlot('transport'),
        hasCore: mech.hasPartInSlot('core'),
        hasHead: mech.hasPartInSlot('head'),
        moveSpeed: (mech as any).getMoveSpeed ? (mech as any).getMoveSpeed() : 'unknown',
        canDash: (mech as any).canDash ? (mech as any).canDash() : 'unknown',
        energyRegen: (mech as any).getEnergyRegen ? (mech as any).getEnergyRegen() : 'unknown',
        hp: mech.getHP(),
        maxHp: mech.getMaxHP(),
        energy: mech.getEnergy(),
        maxEnergy: mech.getMaxEnergy(),
      };
      console.log('=== CURRENT MECH STATE ===');
      console.log(`Parts:`);
      console.log(`  Head: ${state.hasHead ? '✓' : '✗ (DEAD!)'}`);
      console.log(`  Weapon: ${state.hasWeapon ? '✓' : '✗ (can\'t shoot)'}`);
      console.log(`  Core: ${state.hasCore ? '✓' : '✗ (slow energy)'}`);
      console.log(`  Transport: ${state.hasTransport ? '✓' : '✗ (crawl mode)'}`);
      console.log(`Capabilities:`);
      console.log(`  Move Speed: ${state.moveSpeed} units/sec`);
      console.log(`  Can Dash: ${state.canDash}`);
      console.log(`  Energy Regen: ${state.energyRegen} units/sec`);
      console.log(`Resources:`);
      console.log(`  HP: ${state.hp}/${state.maxHp}`);
      console.log(`  Energy: ${state.energy}/${state.maxEnergy}`);
      return state;
    },

    // Test the comeback scenario
    testComeback: () => {
      console.log('');
      console.log('╔════════════════════════════════════════════════╗');
      console.log('║     TESTING COMEBACK SCENARIO (scrap-4eo)      ║');
      console.log('╚════════════════════════════════════════════════╝');
      console.log('');
      console.log('This tests the core loop:');
      console.log('1. Player loses parts → crippled');
      console.log('2. Player kills enemy → parts drop');
      console.log('3. Player picks up parts → functions restore');
      console.log('');

      console.log('--- STEP 1: Equip full loadout ---');
      (window as any).testWeapon.equipWeapon('repeater');
      (window as any).testTransport.equipTransport('treads');
      (window as any).testEnergy.equipCore('standard');
      console.log('');
      (window as any).testPartial.getState();
      console.log('');

      setTimeout(() => {
        console.log('--- STEP 2: Lose transport (simulated damage) ---');
        (window as any).testPartial.breakTransport();
        console.log('');
        console.log('Try moving now - you should crawl at 2 units/sec!');
        console.log('Try dashing - nothing should happen!');
        console.log('');
        (window as any).testPartial.getState();
        console.log('');
      }, 2000);

      setTimeout(() => {
        console.log('--- STEP 3: Lose weapon (simulated damage) ---');
        (window as any).testPartial.breakWeapon();
        console.log('');
        console.log('Try shooting - clicking should do nothing!');
        console.log('');
        (window as any).testPartial.getState();
        console.log('');
      }, 4000);

      setTimeout(() => {
        console.log('--- STEP 4: Lose core (critical damage) ---');
        (window as any).testPartial.breakCore();
        console.log('');
        console.log('Watch energy regen slow to a crawl (1/sec)!');
        console.log('HP pool is reduced!');
        console.log('');
        (window as any).testPartial.getState();
        console.log('');
      }, 6000);

      setTimeout(() => {
        console.log('--- STEP 5: COMEBACK - Re-equip transport ---');
        (window as any).testTransport.equipTransport('legs');
        console.log('');
        console.log('Try moving now - speed restored!');
        console.log('Try dashing - it works again!');
        console.log('');
        (window as any).testPartial.getState();
        console.log('');
      }, 8000);

      setTimeout(() => {
        console.log('--- STEP 6: COMEBACK - Re-equip weapon ---');
        (window as any).testWeapon.equipWeapon('sprayer');
        console.log('');
        console.log('Try shooting - you can attack again!');
        console.log('');
        (window as any).testPartial.getState();
        console.log('');
      }, 10000);

      setTimeout(() => {
        console.log('--- STEP 7: COMEBACK - Re-equip core ---');
        (window as any).testEnergy.equipCore('capacitor');
        console.log('');
        console.log('Watch energy regen speed up!');
        console.log('HP pool restored!');
        console.log('');
        (window as any).testPartial.getState();
        console.log('');
        console.log('╔════════════════════════════════════════════════╗');
        console.log('║     COMEBACK SCENARIO COMPLETE! ✓              ║');
        console.log('╚════════════════════════════════════════════════╝');
        console.log('');
        console.log('You just experienced the core loop of SCRAP:');
        console.log('  → Lose parts, become crippled');
        console.log('  → Kill enemies, loot their parts');
        console.log('  → Equip parts, regain power');
        console.log('');
        console.log('This is what makes SCRAP different from other roguelikes!');
        console.log('Your capabilities are DIRECTLY tied to your parts.');
        console.log('');
      }, 12000);
    },
  };

  // Add test utilities for room generation system
  (window as any).testRoomGen = {
    // Generate a specific floor
    generateFloor: (floorNumber: number) => {
      console.log(`Generating floor ${floorNumber}...`);
      game.generateFloor(floorNumber);
      console.log('Floor generated! Check console for details.');
    },

    // Test room progression
    testProgression: () => {
      console.log('');
      console.log('╔════════════════════════════════════════════════╗');
      console.log('║     TESTING ROOM PROGRESSION                   ║');
      console.log('╚════════════════════════════════════════════════╝');
      console.log('');
      console.log('Watch how rooms scale with difficulty...');
      console.log('');

      let floor = 1;
      const progressFloor = () => {
        if (floor > 10) {
          console.log('');
          console.log('╔════════════════════════════════════════════════╗');
          console.log('║     PROGRESSION TEST COMPLETE!                 ║');
          console.log('╚════════════════════════════════════════════════╝');
          console.log('');
          console.log('Notice how:');
          console.log('  - Rooms get larger');
          console.log('  - More cover appears');
          console.log('  - More enemies spawn');
          console.log('  - Harder enemy types unlock');
          console.log('');
          return;
        }

        console.log(`--- FLOOR ${floor} ---`);
        game.generateFloor(floor);
        floor++;

        setTimeout(progressFloor, 3000);
      };

      progressFloor();
    },

    // Test specific floor configs
    testFloor1: () => game.generateFloor(1),
    testFloor5: () => game.generateFloor(5),
    testFloor10: () => game.generateFloor(10),
  };

  // Add test utilities for floor progression system
  (window as any).testFloorProgression = {
    // Kill all enemies to test room clear
    killAllEnemies: () => {
      console.log('Killing all enemies in the room...');
      for (const entity of (game as any).entities) {
        if (entity.constructor.name === 'Sentry' ||
            entity.constructor.name === 'Runner' ||
            entity.constructor.name === 'Heavy') {
          entity.destroy();
        }
      }
      console.log('All enemies killed! Door should unlock.');
      console.log('Walk to the green exit door and press E to advance.');
    },

    // Get current floor/room
    getCurrentProgress: () => {
      const floor = game.getCurrentFloor();
      const room = game.getCurrentRoom();
      console.log(`Current: Floor ${floor} - Room ${room}/3`);
      return { floor, room };
    },

    // Test full floor progression
    testProgression: () => {
      console.log('');
      console.log('╔════════════════════════════════════════════════╗');
      console.log('║     TESTING FLOOR PROGRESSION (scrap-99l)      ║');
      console.log('╚════════════════════════════════════════════════╝');
      console.log('');
      console.log('This tests:');
      console.log('  - Three rooms per floor');
      console.log('  - Locked exit until room cleared');
      console.log('  - Door interaction to advance');
      console.log('  - Player state preservation between rooms');
      console.log('  - Floor/room counter in HUD');
      console.log('');
      console.log('Use window.testFloorProgression.killAllEnemies() to clear rooms quickly.');
      console.log('Then walk to the green door and press E to advance!');
      console.log('');
    },
  };

  // Add test utilities for fusion system
  (window as any).testFusion = {
    // Test a specific fusion
    testFusion: (partIdA: string, partIdB: string) => {
      const result = getFusionResult(partIdA, partIdB);
      if (result) {
        console.log(`${partIdA} + ${partIdB} = ${result}`);
      } else {
        console.log(`${partIdA} + ${partIdB} = FAILED (no fusion available)`);
      }
      return result;
    },

    // Test all tier-1 fusions
    testAllT1: () => {
      console.log('');
      console.log('╔════════════════════════════════════════════════╗');
      console.log('║         TESTING ALL TIER 1 FUSIONS             ║');
      console.log('╚════════════════════════════════════════════════╝');
      console.log('');

      console.log('=== HEADS ===');
      (window as any).testFusion.testFusion('scanner', 'tracker');
      (window as any).testFusion.testFusion('scanner', 'spotter');
      (window as any).testFusion.testFusion('tracker', 'spotter');
      console.log('');

      console.log('=== WEAPONS ===');
      (window as any).testFusion.testFusion('repeater', 'lancer');
      (window as any).testFusion.testFusion('repeater', 'sprayer');
      (window as any).testFusion.testFusion('lancer', 'sprayer');
      console.log('');

      console.log('=== CORES ===');
      (window as any).testFusion.testFusion('standard', 'capacitor');
      (window as any).testFusion.testFusion('standard', 'bulwark');
      (window as any).testFusion.testFusion('capacitor', 'bulwark');
      console.log('');

      console.log('=== TRANSPORTS ===');
      (window as any).testFusion.testFusion('treads', 'hover');
      (window as any).testFusion.testFusion('treads', 'legs');
      (window as any).testFusion.testFusion('hover', 'legs');
      console.log('');

      console.log('All 12 tier-1 fusions tested!');
    },

    // Spawn fusion test pickups
    spawnFusionTest: (slot: string) => {
      // Clear existing fusion test pickups
      console.log(`Setting up fusion test for ${slot.toUpperCase()} slot...`);
      console.log('');

      if (slot === 'head') {
        console.log('Spawning scanner and tracker pickups.');
        console.log('1. Walk to scanner (right), press E to equip');
        console.log('2. Walk to tracker (left), press F to fuse');
        console.log('3. Result should be Analyst head!');
        game.spawnPickupById(3, 0, 'scanner');
        game.spawnPickupById(-3, 0, 'tracker');
      } else if (slot === 'weapon') {
        console.log('Spawning repeater and lancer pickups.');
        console.log('1. Walk to repeater (right), press E to equip');
        console.log('2. Walk to lancer (left), press F to fuse');
        console.log('3. Result should be Railgun weapon!');
        game.spawnPickupById(3, 0, 'repeater');
        game.spawnPickupById(-3, 0, 'lancer');
      } else if (slot === 'core') {
        console.log('Spawning standard and capacitor pickups.');
        console.log('1. Walk to standard (right), press E to equip');
        console.log('2. Walk to capacitor (left), press F to fuse');
        console.log('3. Result should be Dynamo core!');
        game.spawnPickupById(3, 0, 'standard');
        game.spawnPickupById(-3, 0, 'capacitor');
      } else if (slot === 'transport') {
        console.log('Spawning treads and hover pickups.');
        console.log('1. Walk to treads (right), press E to equip');
        console.log('2. Walk to hover (left), press F to fuse');
        console.log('3. Result should be Glider transport!');
        game.spawnPickupById(3, 0, 'treads');
        game.spawnPickupById(-3, 0, 'hover');
      }
      console.log('');
    },
  };

  console.log('SCRAP initialized. Game loop running at 60 UPS.');
  console.log('Use WASD to move, mouse to aim, hold left click to shoot (requires weapon).');
  console.log('Press E near pickups to swap parts!');
  console.log('Press F near pickups to fuse parts!');
  console.log('');
  console.log('=== FLOOR PROGRESSION SYSTEM (scrap-99l) ===');
  console.log('NEW FEATURES:');
  console.log('  - Three rooms per floor');
  console.log('  - Exit door (red=locked, green=unlocked)');
  console.log('  - Door unlocks when all enemies defeated');
  console.log('  - Press E near unlocked door to advance');
  console.log('  - Smooth fade transitions between rooms');
  console.log('  - Floor/room counter (top-right of screen)');
  console.log('  - Player state preserved between rooms');
  console.log('');
  console.log('Try: window.testFloorProgression.testProgression() for details!');
  console.log('Try: window.testFloorProgression.killAllEnemies() to clear a room quickly!');
  console.log('');
  console.log('=== PROCEDURAL ROOM GENERATION (scrap-dqt) ===');
  console.log('Floor 1 generated with:');
  console.log('  - Procedurally sized room');
  console.log('  - Cover objects (crates, pillars, debris)');
  console.log('  - Enemy spawns based on difficulty');
  console.log('  - Entrance and exit positions');
  console.log('Try window.testRoomGen.testProgression() to see floors 1-10!');
  console.log('');
  console.log('=== Pickup Interact System ===');
  console.log('Test pickups spawned around the arena:');
  console.log('  - Treads (transport) at x=5, y=0 (right)');
  console.log('  - Repeater (weapon) at x=-5, y=0 (left)');
  console.log('  - Standard Core at x=0, y=5 (above)');
  console.log('  - Tracker (head) at x=0, y=-5 (below)');
  console.log('Walk near a glowing pickup and press E to swap!');
  console.log('');
  console.log('=== Fog of War / Head System Testing ===');
  console.log('window.testHead.equipHead("scanner"|"tracker"|"spotter") - Change vision radius');
  console.log('window.testHead.testAll() - Cycle through all head types');
  console.log('Notice the fog circle changing size as you swap heads!');
  console.log('');
  console.log('=== Weapon System Testing ===');
  console.log('window.testWeapon.equipWeapon("repeater"|"lancer"|"sprayer") - Equip weapon');
  console.log('window.testWeapon.removeWeapon() - Remove weapon');
  console.log('window.testWeapon.testAll() - Cycle through all weapons');
  console.log('');
  console.log('=== Energy & HP System Testing ===');
  console.log('window.mech - Direct mech access');
  console.log('window.testEnergy.useEnergy(amount) - Test energy consumption');
  console.log('window.testEnergy.takeDamage(amount) - Test HP damage');
  console.log('window.testEnergy.equipCore("standard"|"capacitor"|"bulwark") - Equip core');
  console.log('window.testEnergy.removeCore() - Remove core');
  console.log('');
  console.log('=== Partial Mech States (scrap-4eo) ===');
  console.log('window.testPartial.breakWeapon() - Remove weapon, test can\'t shoot');
  console.log('window.testPartial.breakTransport() - Remove transport, test crawl mode');
  console.log('window.testPartial.breakCore() - Remove core, test slow energy regen');
  console.log('window.testPartial.getState() - Display current mech capabilities');
  console.log('window.testPartial.testComeback() - Run full comeback scenario!');
  console.log('');
  console.log('=== Fusion System (scrap-r1g) ===');
  console.log('window.testFusion.testAllT1() - Test all 12 tier-1 fusion combinations');
  console.log('window.testFusion.testFusion(partA, partB) - Test a specific fusion');
  console.log('window.testFusion.spawnFusionTest("head"|"weapon"|"core"|"transport") - Spawn fusion test pickups');
  console.log('Walk near a pickup with an equipped part and press F to fuse!');
  console.log('');
  console.log('=== Room Generation System (scrap-dqt) ===');
  console.log('window.testRoomGen.generateFloor(N) - Generate floor N');
  console.log('window.testRoomGen.testFloor1() - Generate floor 1 (easy)');
  console.log('window.testRoomGen.testFloor5() - Generate floor 5 (medium)');
  console.log('window.testRoomGen.testFloor10() - Generate floor 10 (hard)');
  console.log('window.testRoomGen.testProgression() - Auto-cycle through floors 1-10');
  console.log('');
  console.log('Initial state:');
  console.log(`HP: ${mech.getHP()}/${mech.getMaxHP()}`);
  console.log(`Energy: ${mech.getEnergy()}/${mech.getMaxEnergy()}`);
  console.log(`Vision Radius: ${mech.getVisionRadius()} units (Scanner head)`);
  console.log('Watch the top-left HUD for real-time updates!');
  console.log('');
  console.log('Try: window.testPartial.testComeback() to see the core loop in action!');
}

main();
